<template>
    <div>
      <VLowEnvelopeAllowanceWarning />
      <VNoContactsMessage v-if="!results.length && !isSearching" :addContact="openContactNewModal" />
      <div v-else>
        <div class="inline-flex gap-base justify-between justify-right controls-right w-full mb-base flex-wrap-reverse">
          <div class="contacts-search-container flex-grow">
            <VSearch
                class="flex-grow float-none"
                v-if="results.length || isSearching"
                @onSearch="onSearch"
                data-ec="contacts"
                data-et="search_input"
                @onResetSearch="onResetSearch"
                :placeholder="$t('contacts.searchPlaceholder')"
                data-test="contacts-search" />
          </div>
          <VButton
              type="cta-1"
              :action="openContactNewModal"
              v-if="rawResults.length"
              data-test="contacts-add-button"
              data-et="new_contact_top"
              class="float-none whitespace-nowrap">
            <PlusIcon />
            <span>{{ $t('contacts.addContact') }}</span>
          </VButton>
        </div>
        <VViewSwitcher>
          <div slot="card" :title="$t('functional.viewSwitcherButtonOptions.cardView')" icon="address-card">
            <div>
              <VMessage v-if="!results.length" type="info" subtype="outlined" :fullwidth="true">
                <template v-slot:icon>
                  <ExclamationIcon />
                </template>
                <template v-slot:text>
                  {{ $t('contacts.noContacts') }}
                </template>
              </VMessage>
              <TransitionGroup name="fade" class="grid lg:grid-cols-cardGrid3 md:grid-cols-cardGrid2 grid-cols-cardGrid1 gap-base mb-4">
                <VPersonCard
                    :loading="pageLoading"
                    v-for="(contact, index) in results"
                    :key="index"
                    :name="contact.title"
                    :email="contact.email"
                    data-id="contact.id"
                    @click.native="goToContact(contact.id)"
                    class="bg-white"
                    v-cloak >
                  <div slot="extra-content">
                    <div slot="row">
                      <span v-if="!contactsSearchResults.length">{{ $t('contacts.envelopesOutstanding') }}: <b>{{ contact.envelopes_sent_count }}</b></span>
                    </div>
                    <div slot="row">
                      <VButton class="mr-sm mt-sm" size="small" type="outline-1" @click="openEditModal(contact, $event)">
                        <PencilIcon />
                        {{ $t('functional.edit') }}
                      </VButton>
                      <VButton type="outline-error" size="small" class="mr-sm mt-sm bg-error" @click="openDeleteModal(contact, $event)">
                        <TrashIcon />
                        {{ $t('functional.delete') }}
                      </VButton>
                    </div>
                  </div>
                </VPersonCard>
              </TransitionGroup>
            </div>
          </div>
          <div slot="list" :title="$t('functional.viewSwitcherButtonOptions.listView')" icon="th-list">
            <ElTable
              v-loading="loading"
              :data="results"
              :empty-text="$t('contacts.noContacts')">
              <ElTableColumn :label="$t('contacts.contactName')">
                <template slot-scope="scope">
                  <RouterLink
                    :to="{ name: 'ContactStatus', params: { contactId: scope.row.id }}"
                    v-if="scope.row.id"
                    data-ec="contacts"
                    data-et="contact_status">
                    {{ scope.row.title }}
                  </RouterLink>
                </template>
              </ElTableColumn>
              <ElTableColumn :label="$t('contacts.contactEmail')">
                <template slot-scope="scope">
                  {{scope.row.email}}
                </template>
              </ElTableColumn>
              <ElTableColumn
                v-if="!isSearching"
                :label="$t('contacts.envelopesOutstanding')" align="center">
                <template slot-scope="scope">
                  {{ scope.row.envelopes_sent_count }}
                </template>
              </ElTableColumn>
              <ElTableColumn
                :label="$t('contacts.contactActions')"
                width="120">
                <template slot-scope="scope">
                  <ElTooltip class="item" effect="dark" :content="$t('functional.edit')" placement="top-end">
                    <ElButton
                      circle
                      type="outlinePrimary"
                      size="small"
                      icon="el-icon-edit"
                      data-ec="contacts"
                      data-et="edit_contact"
                      @click="openEditModalFromList(scope.row)">
                    </ElButton>
                  </ElTooltip>
                  <ElTooltip class="item" effect="dark" :content="$t('functional.delete')" placement="top-end">
                    <ElButton
                      circle
                      type="outlineDanger"
                      size="small"
                      icon="el-icon-delete"
                      data-ec="contacts"
                      data-et="delete_contact"
                      @click="openDeleteModalFromList(scope.row)">
                    </ElButton>
                  </ElTooltip>
                </template>
              </ElTableColumn>
            </ElTable>
          </div>
        </VViewSwitcher>
      </div>
      <SElPagination
        v-if="results.length && !isSearching"
        :page-count="totalPages"
        :current-page.sync="currentPage"
        :small="isMobileSize" />
      <div class="mt-base">
        <PageResultsCounter
          v-if="resultsMeta && totalCount"
          :from="resultsMeta.from"
          :to="resultsMeta.to"
          :total="totalCount"
          :type="$t('functional.contacts')" />
      </div>
      <NewContactModal :show="showNewModal" @close="showNewModal = false;" />
      <EditContactModal :show="showEditModal" :contact="editContact" @close="showEditModal = false;" />
      <DeleteContactModal :show="showDeleteModal" :contact="deleteContact" @close="showDeleteModal = false;" />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import VLowEnvelopeAllowanceWarning from '@/views/components/LowEnvelopeAllowanceWarning.vue';
import VNoContactsMessage from '@/views/components/NoContactsMessage.vue';
import NewContactModal from '@/views/modals/contact/new.vue';
import EditContactModal from '@/views/modals/contact/edit.vue';
import DeleteContactModal from '@/views/modals/contact/delete.vue';
import SElPagination from '@/views/components/SElPagination.vue';
import PageResultsCounter from '@/views/components/PageResultsCounter.vue';

import VButton from '@/components/deprecated/atoms/Button.vue';
import VMessage from '@/components/deprecated/atoms/Message.vue';
import VPersonCard from '@/components/deprecated/molecules/PersonCard.vue';
import VViewSwitcher from '@/components/deprecated/molecules/ViewSwitcher.vue';
import VSearch from '@/components/deprecated/organisms/Search.vue';
import SearcherMixin from '@/mixins/searcherMixin';
import { ExclamationIcon, PencilIcon, PlusIcon, TrashIcon } from '@/components/icons/fontIcons';

export default {
  name: 'ContactsView',
  inject: {
    $validator: '$validator',
  },
  components: {
    NewContactModal,
    EditContactModal,
    DeleteContactModal,
    VButton,
    VMessage,
    VSearch,
    VLowEnvelopeAllowanceWarning,
    VNoContactsMessage,
    SElPagination,
    VPersonCard,
    VViewSwitcher,
    PageResultsCounter,
    PencilIcon,
    PlusIcon,
    TrashIcon,
    ExclamationIcon,
  },
  mixins: [SearcherMixin('contacts')],
  data() {
    return {
      isSearching: false,
      loading: true,
      currentPage: 1,
      showNewModal: false,
      showEditModal: false,
      showDeleteModal: false,
      editContact: {},
      deleteContact: {},
    };
  },
  created() {
    this.loadData(this.currentPage);
  },
  watch: {
    currentPage(newVal) {
      this.loadData(newVal);
    },
  },
  computed: {
    ...mapGetters({
      totalPages: 'contacts/totalPages',
      rawResults: 'contacts/pagedResults',
      resultsMeta: 'contacts/meta',
      totalCount: 'contacts/totalCount',
    }),
    // TODO clean this up to be in the store. Use paged results to perform searches
    results() {
      if (this.loading) {
        return new Array(60).fill({ title: '', email: '' });
      }
      if (this.contactsSearchResults.length > 0 || this.isSearching) {
        return this.contactsSearchResults;
      }

      return this.rawResults;
    },
    pageLoading() {
      if (this.loading) {
        return true;
      } else if (!this.isSearching && !this.results[0]) {
        return true;
      }

      return false;
    },
    isMobileSize() {
      return !!(screen.width < 450); // eslint-disable-line
    },
  },
  methods: {
    goToContact(id) {
      this.$router.push(`contacts/${ id }`);
    },
    loadData(page) {
      this.loading = true;
      this.$store.dispatch('contacts/PAGINATE', { page, pageLimit: 60 })
      .finally(() => {
        this.loading = false;
      });
    },
    openContactNewModal() {
      this.showNewModal = true;
    },
    openEditModal(contact, event) {
        event.stopPropagation();
        this.editContact = contact;
        this.showEditModal = true;
    },
    openDeleteModal(contact, event) {
      event.stopPropagation();
      this.deleteContact = contact;
      this.showDeleteModal = true;
    },
    openEditModalFromList(contact) {
      this.editContact = contact;
      this.showEditModal = true;
    },
    openDeleteModalFromList(contact) {
      this.deleteContact = contact;
      this.showDeleteModal = true;
    },
    async onSearch({ value }) {
      this.loading = true;
      this.isSearching = true;
      await this.searchContacts({ query: value });
      this.loading = false;
    },
    onResetSearch() {
      this.isSearching = false;
      this.contactsSearchResults = [];
    },
  },
};
</script>

<style scoped>
.o-search {
  float: none;
}

.o-search .a-input {
  float: none !important;
}

.controls-right {
  justify-content: right;
}
</style>
